import {SheetModal} from 'app/pages/school/Schedule/components/timetable/pages/workLoadBreakDown/modals/components/SheetModal'
import {
  curriculumCodeFor,
  curriculumPrefixes,
  stripCurriculumPrefix,
} from 'common/helpers/curriculumDisplay'
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Building2,
  CalendarDays,
  Clock,
  Coffee,
  Pencil,
  Plus,
  Presentation,
} from 'lucide-react'
import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'

import {
  InstitutionDefaults,
  groupBreakTimingsByDay,
  loadCurriculumDefaults,
  prefillInstitutionDefaults,
  readStoredCurriculumPrefills,
  summarizeBreakTimings,
  timingsFromLevel,
} from '../curriculumPrefill'
import {OnboardingPartialSubmit, WizardData} from '../index'
import {ONBOARDING_QA_A11Y} from '../utils/onboardingQaA11y'
import {ONBOARDING_QA_OCT18} from '../utils/onboardingQaOct18'
import {
  DEFAULT_BRANCH_NAME,
  DEFAULT_LECTURE_DURATION,
  DEFAULT_SUBJECT_FREQUENCY,
  DEFAULT_TIMING_DAYS,
  DEFAULT_TIMING_END,
  DEFAULT_TIMING_START,
  WEEKDAYS,
  formatTime24to12,
  getSeasonFromCurrentDate,
} from './Step2BasicSetup'
import Step2SectionEditor from './Step2SectionEditor'

/** The detailed-setup sections an Edit button can open (order = old sub-step order). */
const SECTION_FIELDS = ['branches', 'duration', 'frequency', 'venues', 'timings'] as const
/** 'breaks' is sheet-only — the review lists it as its own row, apart from timings. */
type SectionField = (typeof SECTION_FIELDS)[number] | 'breaks'

/** One placeholder per review row while the curriculum prefill is in flight. */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5] as const
/** Keep the skeleton on screen at least this long — a sub-100ms answer otherwise just flickers. */
const MIN_SKELETON_MS = 450

interface Props {
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  /** Deep-link entry: old /school-setup/<sub-step> URLs open that section's edit sheet. */
  initialSubStep?: number
  /** Reports the open section's field id (or null when back on the review) for URL sync. */
  onActiveSubStepChange?: (field: string | null) => void
  onNext?: () => void
  onBack?: () => void
  onPartialSubmit?: OnboardingPartialSubmit
  isSubmitting?: boolean
  /**
   * True until the wizard has merged the saved setup (preferences + GET /onboarding/data) into
   * `data`. Step 2 mounts as soon as preferences load, which is BEFORE that merge on resume, so
   * anything here that reads `data.manualData` once at mount (the default seeder, the edit
   * sheet's local draft) would otherwise capture an empty snapshot and hide the saved timings,
   * session, venues and breaks.
   */
  isHydrating?: boolean
}

/**
 * Step 2 — "Institution Setup" as a single review page: every sub-step of the
 * old flow is pre-filled with a recommended default and summarised as one row;
 * an Edit button opens the existing detailed setup UI (Step2BasicSetup with a
 * single section) in a right-side slide-over sheet. Breaks share the timings
 * section, where the existing break-timings UI lives.
 */
const Step2Review: FC<Props> = ({
  data,
  updateData,
  isDark,
  initialSubStep,
  onActiveSubStepChange,
  onNext,
  onBack,
  onPartialSubmit,
  isSubmitting = false,
  isHydrating = false,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [openSection, setOpenSection] = useState<SectionField | null>(
    initialSubStep !== undefined ? (SECTION_FIELDS[initialSubStep] ?? null) : null
  )

  const md = data.manualData
  // Names only: state persisted by an older build may still hold `{name, id}` entries.
  const branches = ((md?.branches ?? []) as unknown[])
    .map((entry: any) => String(typeof entry === 'string' ? entry : (entry?.name ?? '')).trim())
    .filter(Boolean)
  const timings = md?.timings ?? []
  const breakTimings: any[] = (md as any)?.break_timings ?? []
  const venuesSameForClass = md?.venuesSameForClass ?? null
  const frequency = md?.defaultSubjectFrequency ?? DEFAULT_SUBJECT_FREQUENCY
  const frequencyPer = md?.defaultFrequencyPer ?? 'week'

  // Latest manualData for the async seed below, so a prefill that lands after the
  // user already opened an editor merges onto what they have, not a stale snapshot.
  const manualDataRef = useRef(data.manualData)
  useEffect(() => {
    manualDataRef.current = data.manualData
  })

  // Seed every recommended default once so the page reviews a complete setup.
  // All prefills: none of these count as user input for the onboarding funnel.
  // Session, school timings and breaks come from the curriculum prefill (GET
  // timetable/prefill for the first selected curriculum that has a catalog code)
  // when it answers; the flat wizard defaults stand in when it cannot (no code,
  // request failed, no timings for that curriculum). The prefill's provenance is
  // kept in `prefillSources` so the rows can say how much to trust each value.
  // The basis of the seed is "<country>|<curriculum code>" (`institutionPrefillKey`,
  // persisted with the curriculum config). A different basis on this visit — the
  // country or curriculum changed since the page was last seeded — replaces
  // session, hours and breaks wholesale from the new prefill: a new basis is a
  // reset, not an edit, and everything stays editable afterwards. The same basis
  // only fills fields that are still empty, so a resume keeps its saved values.
  // Waits for hydration: on resume the saved setup lands in `data` after this page mounts, and
  // seeding from the pre-hydration snapshot would either fill "empty" fields the user already
  // saved or, worse, compute a stale `basisChanged` and replace the saved hours wholesale.
  const seededRef = useRef(false)
  const [isSeeding, setIsSeeding] = useState(false)
  useEffect(() => {
    if (isHydrating || seededRef.current) return
    seededRef.current = true
    const initial = data.manualData ?? ({} as WizardData['manualData'])

    const codes = initial.curriculumCodes ?? {}
    const primary = (initial.curriculums ?? []).find((name) => Boolean(codes[name]))
    const savedKey = initial.institutionPrefillKey ?? null
    // The country is detected/picked on Setup Basics only, so a resume that lands past that
    // screen may not have it in state yet (it is not always persisted). The saved key already
    // carries the country it was seeded with; reuse it rather than reading "no country" as a
    // country change — that replaced the user's saved hours/session/breaks with the prefill's.
    const savedCountry = savedKey && savedKey !== 'defaults' ? savedKey.split('|')[0] : ''
    const country = data.country ?? savedCountry
    const prefillKey = primary ? `${country}|${codes[primary]}` : 'defaults'
    const basisChanged = primary !== undefined && savedKey !== prefillKey

    const seed = (defaults: InstitutionDefaults | null) => {
      const current = manualDataRef.current ?? initial
      const next: Partial<WizardData['manualData']> = {}
      const sessionEmpty =
        !current.durationName?.trim() && !current.durationStartDate && !current.durationEndDate
      const timingsEmpty = (current.timings ?? []).length === 0
      const breaksEmpty = (current.break_timings ?? []).length === 0
      // New basis with a prefill in hand: replace. Otherwise only fill what is empty.
      const replace = defaults !== null && basisChanged
      const seedSession = sessionEmpty || (replace && defaults.session !== null)
      const seedTimings = timingsEmpty || (replace && defaults.timings.length > 0)
      const seedBreaks = defaults !== null && (breaksEmpty ? defaults.breaks.length > 0 : replace)
      if ((current.branches ?? []).length === 0) next.branches = [DEFAULT_BRANCH_NAME]
      if (seedSession) {
        const season = defaults?.session ?? getSeasonFromCurrentDate()
        next.durationName = season.name
        next.durationStartDate = season.start
        next.durationEndDate = season.end
      }
      if (current.venuesSameForClass == null) next.venuesSameForClass = true
      if (seedTimings) {
        next.timings = (
          defaults && defaults.timings.length > 0
            ? defaults.timings
            : DEFAULT_TIMING_DAYS.map((day) => ({
                day,
                startTime: DEFAULT_TIMING_START,
                endTime: DEFAULT_TIMING_END,
                lectureDuration: DEFAULT_LECTURE_DURATION,
              }))
        ) as any
      }
      if (seedBreaks && defaults) next.break_timings = defaults.breaks
      if (current.defaultSubjectFrequency == null) {
        next.defaultSubjectFrequency = DEFAULT_SUBJECT_FREQUENCY
      }
      if (current.defaultFrequencyPer == null) next.defaultFrequencyPer = 'week'
      if (defaults && (seedSession || seedTimings)) {
        next.prefillSources = defaults.sources
        next.schoolWeekNote = defaults.schoolWeekNote ?? undefined
      }
      if (defaults) next.institutionPrefillKey = prefillKey
      else if (!initial.institutionPrefillKey) next.institutionPrefillKey = 'defaults'
      if (Object.keys(next).length > 0) {
        updateData({manualData: {...current, ...next}}, {prefill: true})
      }
      setIsSeeding(false)
    }

    const sessionEmptyNow =
      !initial.durationName?.trim() && !initial.durationStartDate && !initial.durationEndDate
    const timingsEmptyNow = (initial.timings ?? []).length === 0
    if (!primary || (!basisChanged && !sessionEmptyNow && !timingsEmptyNow)) {
      seed(null)
      return
    }
    // Already fetched (Academic Structure, or an earlier visit here): no round trip.
    const cached = readStoredCurriculumPrefills()[primary]
    if (cached) {
      seed(prefillInstitutionDefaults(cached))
      return
    }
    setIsSeeding(true)
    const startedAt = Date.now()
    loadCurriculumDefaults(
      dispatch,
      {country: data.country, curriculums: [primary], codes},
      (_details, prefills) => {
        const prefill = prefills[primary]
        const wait = Math.max(0, MIN_SKELETON_MS - (Date.now() - startedAt))
        window.setTimeout(() => seed(prefill ? prefillInstitutionDefaults(prefill) : null), wait)
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrating])

  const openEditor = (field: SectionField) => {
    setOpenSection(field)
    // 'breaks' is not a step-tree sub-step (its historical home is timings), so
    // report timings for the URL slug and the persisted resume pointer.
    onActiveSubStepChange?.(field === 'breaks' ? 'timings' : field)
  }
  const closeEditor = () => {
    setOpenSection(null)
    onActiveSubStepChange?.(null)
  }

  useEffect(() => {
    onActiveSubStepChange?.(openSection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dayName = (day: string) => intl.formatMessage({id: `DAY.${day.toUpperCase()}` as any})

  const formatSessionDate = (iso: string) => {
    if (!iso) return ''
    const d = new Date(`${iso}T12:00:00`)
    if (isNaN(d.getTime())) return iso
    return new Intl.DateTimeFormat(intl.locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d)
  }

  const timingsSummary = useMemo(() => {
    if (timings.length === 0) return null
    const days = [...new Set(timings.map((t: any) => String(t.day ?? '').toUpperCase()))]
      .filter((d) => (WEEKDAYS as readonly string[]).includes(d))
      .sort((a, b) => WEEKDAYS.indexOf(a as any) - WEEKDAYS.indexOf(b as any))
    const contiguous =
      days.length > 1 &&
      days.every(
        (d, i) => i === 0 || WEEKDAYS.indexOf(d as any) === WEEKDAYS.indexOf(days[i - 1] as any) + 1
      )
    const daysLabel = contiguous
      ? `${dayName(days[0])} – ${dayName(days[days.length - 1])}`
      : days.map(dayName).join(', ')

    const starts = new Set(timings.map((t: any) => t.startTime))
    const ends = new Set(timings.map((t: any) => t.endTime))
    const spanLabel =
      starts.size === 1 && ends.size === 1
        ? `${formatTime24to12(timings[0].startTime)} — ${formatTime24to12(timings[0].endTime)}`
        : intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2_REVIEW.TIMINGS_VARIES'})

    const durations = new Set(timings.map((t: any) => t.lectureDuration))
    const lessonsLabel =
      durations.size === 1 && timings[0].lectureDuration
        ? intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2_REVIEW.LESSON_LENGTH'},
            {minutes: timings[0].lectureDuration}
          )
        : null

    return [daysLabel, spanLabel, lessonsLabel].filter(Boolean).join('  •  ')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timings, intl])

  // "Recommended based on Pakistan + Cambridge (CAIE)." Only catalog boards (the ones with
  // an API code) are a basis for the recommendation; a name typed under "Other" has no
  // code and no prefill behind it, so it is listed apart, as a new curriculum.
  const curriculumCodes = md?.curriculumCodes ?? {}
  const catalogCurriculums = (md?.curriculums ?? []).filter((name) =>
    Boolean(curriculumCodes[name])
  )
  const newCurriculums = (md?.curriculums ?? []).filter((name) => !curriculumCodes[name])
  const recommendContext = [data.country, ...catalogCurriculums].filter(Boolean).join(' + ')

  const sessionValid =
    !!md?.durationName?.trim() &&
    !!md?.durationStartDate &&
    !!md?.durationEndDate &&
    md.durationEndDate > md.durationStartDate

  const nextDisabled =
    isSubmitting ||
    isHydrating ||
    isSeeding ||
    branches.length === 0 ||
    !sessionValid ||
    timings.length === 0 ||
    venuesSameForClass === null ||
    frequency < 1 ||
    frequency > 20

  const handleNextClick = () => {
    // Parity with the old flow, where leaving the Branches sub-step synced them.
    if (onPartialSubmit) {
      onPartialSubmit(['branches'], data.manualData, () => onNext?.())
    } else {
      onNext?.()
    }
  }

  const t = (suffix: string, values?: Record<string, any>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP2_REVIEW.${suffix}` as any}, values)

  // Provenance of the seeded school hours (prefill `sources.timings`): researched for
  // this country, the regional pattern, or the flat placeholder — anything short of
  // curated is said out loud, because a wrong prefill does not fail, it just looks
  // like a plausible school day. Null when the hours did not come from the prefill.
  const timingsSourceNote = (() => {
    switch (md?.prefillSources?.timings) {
      case 'curated':
        return t('SOURCE_TIMINGS_CURATED', {country: data.country ?? ''})
      case 'regional':
        return t('SOURCE_TIMINGS_REGIONAL')
      case 'default':
        return t('SOURCE_TIMINGS_DEFAULT')
      default:
        return null
    }
  })()
  // e.g. "Friday is a half day (since Jan 2022)" — a half day is a school day that
  // ends early, and the prefill carries no early end time, so it is said instead.
  const schoolWeekNote = md?.schoolWeekNote?.trim() || null

  // P7-3: the wizard keeps one set of hours, taken from one level of the primary curriculum's
  // prefill. When other levels keep other hours, say which level these came from (only while
  // the hours on screen are still that level's). Read from the cached prefill the seed used.
  const timingsLevel = useMemo(() => {
    const codes = md?.curriculumCodes ?? {}
    const primary = (md?.curriculums ?? []).find((name) => Boolean(codes[name]))
    const cached = primary ? readStoredCurriculumPrefills()[primary] : undefined
    const level = cached ? timingsFromLevel(prefillInstitutionDefaults(cached), timings) : null
    if (!primary || !cached || !level) return null
    // The plain level name; with several curricula, the curriculum's code says whose level.
    const plain = stripCurriculumPrefix(
      level,
      curriculumPrefixes(
        primary,
        cached.levels.map((lvl) => lvl.level),
        cached.curriculumShortCode
      )
    )
    return (md?.curriculums ?? []).length > 1
      ? `${curriculumCodeFor(primary, cached.curriculumShortCode)} ${plain}`
      : plain
    // isSeeding: the cache is filled by the seed's fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [md?.curriculums, md?.curriculumCodes, timings, isSeeding])

  // P7-4: break rows are one per day × break; the summary counts the breaks of a day and names
  // their times and days ("1 break a day (10:30-10:50), Mon-Fri").
  const shortDayName = (day: string) =>
    intl.formatDate(new Date(2024, 0, 1 + WEEKDAYS.indexOf(day as any), 12), {weekday: 'short'})
  /** "Mon-Fri", or "Mon, Wed" when the days are not one run. */
  const dayRange = (days: string[], contiguous: boolean) =>
    contiguous
      ? `${shortDayName(days[0])}-${shortDayName(days[days.length - 1])}`
      : days.map(shortDayName).join(', ')
  const breaksSummary = (() => {
    if (breakTimings.length === 0) return t('BREAKS_NONE')
    // QA: days whose breaks differ are said apart ("Mon: 2 breaks (…); Tue-Fri: 1 break (…)"),
    // not as one week of the most breaks any day has.
    const groups = ONBOARDING_QA_OCT18 ? groupBreakTimingsByDay(breakTimings) : []
    if (groups.length > 1) {
      return groups
        .map((group) =>
          t('BREAKS_SUMMARY_GROUP', {
            count: group.count,
            times: group.times.join(', '),
            days: dayRange(group.days, group.contiguous),
          })
        )
        .join('; ')
    }
    const summary = summarizeBreakTimings(breakTimings)
    if (summary.count === 0 || summary.days.length === 0) {
      return t('BREAKS_COUNT', {count: summary.count || breakTimings.length})
    }
    const days = summary.contiguous
      ? `${shortDayName(summary.days[0])}-${shortDayName(summary.days[summary.days.length - 1])}`
      : summary.days.map(shortDayName).join(', ')
    return t('BREAKS_SUMMARY', {count: summary.count, times: summary.times.join(', '), days})
  })()

  const sheetTitleByField: Record<SectionField, string> = {
    branches: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES'}),
    duration: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION'}),
    frequency: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY'}),
    venues: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES'}),
    timings: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS'}),
    breaks: t('BREAKS_TITLE'),
  }

  const rows: Array<{
    key: string
    field: SectionField
    icon: React.ReactNode
    title: string
    summary: string
    note: string
    /** Second note line under `note` (own line so it does not read as part of the basis). */
    extraNote?: string
    actionLabel: string
  }> = [
    {
      key: 'branches',
      field: 'branches',
      icon: <Building2 size={20} />,
      title: t('BRANCH_TITLE'),
      // "Assumed" only while the default branch stands in; a single branch the
      // user renamed/replaced is real data and is shown by name.
      summary:
        branches.length === 0 || (branches.length === 1 && branches[0] === DEFAULT_BRANCH_NAME)
          ? t('BRANCH_SINGLE')
          : branches.length === 1
            ? t('BRANCH_SINGLE_NAMED', {name: branches[0]})
            : t('BRANCH_MULTI', {count: branches.length, names: branches.join(', ')}),
      note: t('BRANCH_NOTE'),
      actionLabel: intl.formatMessage({id: 'GENERAL.EDIT'}),
    },
    {
      key: 'session',
      field: 'duration',
      icon: <CalendarDays size={20} />,
      title: t('SESSION_TITLE'),
      summary: sessionValid
        ? `${md?.durationName ?? ''}  •  ${formatSessionDate(md?.durationStartDate ?? '')} — ${formatSessionDate(md?.durationEndDate ?? '')}`
        : t('SESSION_MISSING'),
      note: recommendContext
        ? t('RECOMMENDED_BASED_ON', {context: recommendContext})
        : t('SESSION_NOTE'),
      extraNote:
        newCurriculums.length > 0
          ? t('SESSION_NEW_CURRICULUM', {
              count: newCurriculums.length,
              names: newCurriculums.join(', '),
            })
          : undefined,
      actionLabel: intl.formatMessage({id: 'GENERAL.EDIT'}),
    },
    {
      key: 'frequency',
      field: 'frequency',
      icon: <BarChart3 size={20} />,
      title: t('FREQUENCY_TITLE'),
      summary:
        frequencyPer === 'day'
          ? t('FREQUENCY_SUMMARY_DAY', {count: frequency})
          : t('FREQUENCY_SUMMARY_WEEK', {count: frequency}),
      note: t('FREQUENCY_NOTE'),
      actionLabel: intl.formatMessage({id: 'GENERAL.EDIT'}),
    },
    {
      key: 'venues',
      field: 'venues',
      icon: <Presentation size={20} />,
      title: t('VENUE_TITLE'),
      summary: venuesSameForClass === false ? t('VENUE_ROTATING') : t('VENUE_FIXED'),
      note: t('VENUE_NOTE'),
      actionLabel: intl.formatMessage({id: 'GENERAL.EDIT'}),
    },
    {
      key: 'timings',
      field: 'timings',
      icon: <Clock size={20} />,
      title: t('TIMINGS_TITLE'),
      summary: timingsSummary ?? t('TIMINGS_MISSING'),
      note: [timingsSourceNote ?? t('TIMINGS_NOTE'), schoolWeekNote].filter(Boolean).join(' '),
      extraNote: timingsLevel
        ? intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_FROM_LEVEL' as any},
            {level: timingsLevel}
          )
        : undefined,
      actionLabel: intl.formatMessage({id: 'GENERAL.EDIT'}),
    },
    {
      key: 'breaks',
      field: 'breaks',
      icon: <Coffee size={20} />,
      title: t('BREAKS_TITLE'),
      summary: breaksSummary,
      note: breakTimings.length > 0 && timingsSourceNote ? timingsSourceNote : t('BREAKS_NOTE'),
      actionLabel: intl.formatMessage({
        id: breakTimings.length > 0 ? 'GENERAL.EDIT' : 'GENERAL.ADD',
      }),
    },
  ]

  const iconBubbleStyle: React.CSSProperties = {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#056EE9',
    backgroundColor: isDark ? 'rgba(5,110,233,0.15)' : 'rgba(5,110,233,0.08)',
  }

  return (
    <div className='d-flex flex-column step2-review' style={{gap: '1.25rem', paddingTop: '1.5rem'}}>
      <div>
        <h2
          style={{
            color: isDark ? '#ffffff' : '#181C32',
            fontSize: '1.6rem',
            fontWeight: 700,
            marginBottom: '0.4rem',
          }}
        >
          {t('HEADER_TITLE')}
        </h2>
        <p
          style={{
            color: isDark ? '#92929f' : '#7E8299',
            fontSize: '1rem',
            lineHeight: 1.6,
            marginBottom: 0,
          }}
        >
          {t('HEADER_SUBTITLE')}
        </p>
      </div>

      <div
        className='step2-review-card'
        style={{
          borderRadius: 14,
          border: `1px solid ${isDark ? '#2b2b40' : '#dee2e6'}`,
          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
        }}
      >
        {isHydrating || isSeeding
          ? // Saved setup still loading (resume) or prefill in flight: placeholder rows, then
            // the hydrated/seeded values.
            SKELETON_ROWS.map((idx) => (
              <div
                key={idx}
                className='step2-review-row'
                aria-hidden
                style={{
                  borderTop: idx === 0 ? 'none' : `1px solid ${isDark ? '#2b2b40' : '#eef0f4'}`,
                }}
              >
                <span className='step2-review-skeleton step2-review-skeleton--icon' />
                <div className='step2-review-row-title'>
                  <span className='step2-review-skeleton step2-review-skeleton--title' />
                </div>
                <div className='step2-review-row-body'>
                  <span className='step2-review-skeleton step2-review-skeleton--summary' />
                  <span className='step2-review-skeleton step2-review-skeleton--note' />
                </div>
                <span className='step2-review-skeleton step2-review-skeleton--btn' />
              </div>
            ))
          : rows.map((row, idx) => (
              <div
                key={row.key}
                className='step2-review-row'
                style={{
                  borderTop: idx === 0 ? 'none' : `1px solid ${isDark ? '#2b2b40' : '#eef0f4'}`,
                }}
              >
                <div style={iconBubbleStyle}>{row.icon}</div>
                <div className='step2-review-row-title'>
                  <span style={{color: isDark ? '#ffffff' : '#181C32'}}>{row.title}</span>
                </div>
                <div className='step2-review-row-body'>
                  <div
                    style={{
                      color: isDark ? '#ffffff' : '#181C32',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    }}
                  >
                    {row.summary}
                  </div>
                  <div style={{color: isDark ? '#7e8299' : '#7E8299', fontSize: '0.85rem'}}>
                    {row.note}
                  </div>
                  {row.extraNote && (
                    <div style={{color: isDark ? '#b5b5c3' : '#5e6278', fontSize: '0.85rem'}}>
                      {row.extraNote}
                    </div>
                  )}
                </div>
                <button
                  type='button'
                  className='step2-review-edit-btn'
                  aria-label={row.actionLabel}
                  title={row.actionLabel}
                  onClick={() => openEditor(row.field)}
                >
                  {row.actionLabel === intl.formatMessage({id: 'GENERAL.ADD'}) ? (
                    <Plus size={15} />
                  ) : (
                    <Pencil size={15} />
                  )}
                </button>
              </div>
            ))}
      </div>

      <div className='d-flex justify-content-between mt-2'>
        <button
          type='button'
          className='onboarding-btn-back'
          disabled={isSubmitting}
          onClick={onBack}
        >
          <ArrowLeft size={16} className='me-2' />
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
        </button>
        <button
          type='button'
          className='onboarding-btn-next'
          disabled={nextDisabled}
          onClick={handleNextClick}
        >
          {isSubmitting ? (
            <>
              <span className='spinner-border spinner-border-sm align-middle me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SAVING'})}
            </>
          ) : (
            intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.NEXT'})
          )}
          <ArrowRight size={16} className='ms-2' />
        </button>
      </div>

      <SheetModal
        show={openSection !== null}
        onHide={closeEditor}
        title={openSection ? sheetTitleByField[openSection] : ''}
        icon=''
        elementIcon={
          openSection ? (
            <span style={{color: '#056EE9', display: 'inline-flex'}}>
              {rows.find((r) => r.field === openSection)?.icon}
            </span>
          ) : undefined
        }
        showCloseButton
        maxWidth='480px'
        // Wizard gradient surface + footer/button styling, see _onboardingwizard.scss.
        sheetClassName='step2-edit-sheet'
        // Squeeze the review page aside instead of dimming it (desktop only, see scss).
        push
        // QA 6c: named dialog, focus in/out, Tab kept inside, Escape closes.
        accessibleDialog={ONBOARDING_QA_A11Y}
      >
        {/* Sheet-native editors working on a local draft: Save commits the section
            in one updateData call (branches also run the incremental sync, like
            the old sub-step advance); closing without saving discards the draft.
            Not mounted while hydrating: a resume that lands with a sheet open (the
            saved sub-step pointer) would otherwise copy the empty pre-hydration
            section into the draft and show nothing even after the saved values land. */}
        {openSection && !isHydrating && (
          <Step2SectionEditor
            key={openSection}
            section={openSection}
            data={data}
            updateData={updateData}
            isDark={isDark}
            onDone={closeEditor}
            onPartialSubmit={onPartialSubmit}
            isSubmitting={isSubmitting}
          />
        )}
      </SheetModal>
    </div>
  )
}

export default Step2Review
